/* eslint-disable react-hooks/exhaustive-deps */
import { pdf } from '@react-pdf/renderer';
import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import CompleteFeedbackButton from '../../components/button/CompleteFeedbackButton/CompleteFeedbackButton';
import GeneratePDF from '../../components/canvas/pdf/GeneratePDF';
import Loading from '../../components/general/Loading';
import ReturnPageButton from '../../components/general/ReturnButton';
import { errorToast, successToast } from '../../components/general/ToasterContainer';
import UserTable from '../../components/feedback/UserTable';
import { formatDateToView } from '../../functions/DateTime';
import { getFacilitator, getParticipants, getRequester, userRole } from '../../functions/FilterUsers';
import AuthenticatedLayout from "../../layout/AuthenticatedLayout";
import * as FeedbackService from "../../services/FeedbackService";
import { t } from 'i18next';

const FeedbackShow = (): React.JSX.Element => {
  let { id } = useParams();
  const [facilitator, setFacilitator] = useState<IUser>({} as IUser)
  const [participants, setParticipants] = useState<IUser[]>([])
  const [feedbackCanvas, setFeedbackCanvas] = useState<IFeedback>({} as IFeedback)
  const [loading, setLoading] = useState<boolean>(true)
  const[submitStatus, setSubmitStatus] = useState<Record<string, "idle" | "submitting" | "submitted">>({});
  const isFeedbackEditable = feedbackCanvas.status !== "completed"

  const loadFeedback = useCallback(async () => {
    const getError = (data: any): void => {
      console.log("Erro ao carregar dados do feedback:", data)
      setLoading(false)
    }

    const getSuccess = (data: any): void => {
      console.log("Dados do feedback carregado:", data)
      setFeedbackCanvas(data)
      setLoading(false)
    }

    if (id) {
      await FeedbackService.get(id, getSuccess, getError)
    } else {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    if (feedbackCanvas.users) {
      setFacilitator(getFacilitator(feedbackCanvas.users) as IUser)
      setParticipants(getParticipants(feedbackCanvas.users))
    }
  }, [feedbackCanvas])

  useEffect(() => {
    loadFeedback()
  }, [])

  const resendEmail = (user: IUser): void => {
    setSubmitStatus((prev) => ({ ...prev, [user.id]: "submitting" }));

    FeedbackService.resendEmail(
      feedbackCanvas.id,
      { person_id: user.id, invitation_code: user.invitation?.code },
      () => {
        successToast({ message: 'Email enviado com sucesso!' });
        setSubmitStatus((prev) => ({ ...prev, [user.id]: "submitted" }));
      },
      () => {
        errorToast({ message: 'Erro na solicitação de envio do Email' });
        setSubmitStatus((prev) => ({ ...prev, [user.id]: "idle" }));
      }
    );
  };


  const onDownload = async () => {
    try {
      const blob = await pdf(<GeneratePDF feedback={feedbackCanvas} cards={feedbackCanvas.cards || []} />).toBlob();
      const url = URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.download = `Quadro de ${getRequester(feedbackCanvas.users)?.name}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao gerar ou baixar o PDF', err);
    }
  };

  if (loading) return (
    <AuthenticatedLayout>
      <div id="feedback-list" className="d-flex flex-column align-items-center justify-content-center">
        <Loading title={t("loading.canvas")} />
      </div>
    </AuthenticatedLayout>
  )

  const organizer = ["requester", "facilitator"].includes(userRole(feedbackCanvas) || "");
  const facilitatorRole = userRole(feedbackCanvas) === "facilitator";

  return (
    <AuthenticatedLayout>
      <div className="d-flex justify-content-center align-self-center">
        <div className="d-flex flex-column w-75 gap-3">
          <h1 className="mb-3">{t("feedbackShow.title.main")}</h1>
          <div className='d-flex justify-content-between align-items-center'>
            <div className='d-flex gap-2 align-items-center'>
              <span className='h-100'> <strong>{t("feedbackDate.message.date")}:</strong> {formatDateToView(feedbackCanvas.schedule_date)}</span>
              |
              <span className='h-100'><strong>{t("feedbackDate.message.time")}:</strong> {feedbackCanvas.schedule_time}</span>
            </div>
            <div className='d-flex gap-3'>
              <ReturnPageButton textButton={t("buttonActions.back")} redirectTo="/feedbacks" />
              { organizer && (
                <>
                  { isFeedbackEditable && <Link to={`/feedbacks/${feedbackCanvas.id}/edit`} className="btn btn-primary">{t("buttonActions.edit")}</Link>}
                  <div className="btn btn-primary" onClick={() => onDownload()}>
                    {t("buttonActions.download")}
                  </div>
                { facilitatorRole && <CompleteFeedbackButton
                  feedbackCanvas={feedbackCanvas}
                  onStatusUpdate={setFeedbackCanvas}
                />}
                </>
              )}
            </div>
          </div>

          <UserTable
            title={t("feedbackRoles.facilitator")}
            users={[facilitator]}
            organizer={organizer}
            submitStatus={submitStatus}
            onResendEmail={resendEmail}
          />
          <UserTable
            title={t("feedbackRoles.participants")}
            users={participants}
            organizer={organizer}
            submitStatus={submitStatus}
            onResendEmail={resendEmail}
          />
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

export default FeedbackShow
